/**
 * PermissionFailureBanner — makes a failed permission fetch visible.
 *
 * PermissionGate and PermissionRoute fail closed: when /my-permissions cannot be
 * reached the permission set is empty and every control disappears. That is the
 * correct security behaviour, but on its own it is indistinguishable from "you
 * are not allowed to do anything" — the console silently degrades to read-only
 * with nothing but a console.warn (VAPT finding N7).
 *
 * Mount this once per app, inside the authenticated layout. It renders nothing
 * unless the fetch actually failed.
 *
 * Note: PermissionContext sets permissionsLoaded = true in its `finally`, even
 * on failure, so a "still loading" state never covers this case. This banner is
 * the only thing that tells the user their view is degraded rather than denied.
 */

import { usePermissions } from './PermissionContext';

export default function PermissionFailureBanner() {
    const { permissionsFailed, refreshPermissions } = usePermissions();

    if (!permissionsFailed) return null;

    return (
        <div
            role="alert"
            className="flex items-center justify-between gap-4 px-4 py-3 bg-amber-50 border-b border-amber-200"
        >
            <p className="text-sm text-amber-900">
                Your permissions could not be verified, so actions are hidden. This view may be
                incomplete.
            </p>
            <button
                onClick={refreshPermissions}
                className="shrink-0 px-3 py-1.5 text-sm font-medium text-amber-900 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors"
            >
                Retry
            </button>
        </div>
    );
}
